/**
 * Pure geometry for TacticalMapPanel — token derivation and grid bounds.
 *
 * Split out of the panel so the arithmetic can be tested without a DOM: the
 * bounds math is where a Large creature at the edge gets clipped, and that is
 * not something a render test would catch legibly.
 */

import type { GameCharacterInfo } from '@danda/shared';

/** One cell of the rendered grid, in feet. SRD 5.2: one square is 5 feet. */
export const FEET_PER_SQUARE = 5;
/** Empty cells kept around the outermost token, so nobody sits on the border. */
export const MARGIN_CELLS = 1;
/** Grid never renders smaller than this, or a solo token looks like a bug. */
export const MIN_SPAN_CELLS = 5;

/**
 * SRD 5.2 creature space (combat.md:93). Large and up occupy a square block of
 * cells; Tiny and Small/Medium each occupy one. Tiny actually packs 4 per square,
 * which the engine does not model either, so one cell is the honest rendering.
 */
const FOOTPRINT_BY_SIZE: Record<string, number> = {
  tiny: 1,
  small: 1,
  medium: 1,
  large: 2,
  huge: 3,
  gargantuan: 4,
};

/**
 * How a token reads on the board. Party membership outranks attitude, since a
 * teammate is a teammate whatever they think of you.
 *
 * Mirrors the attitude palette the scene panel already uses
 * (`SceneCharsSection.getAttitudeBadge`) rather than inventing a second one:
 * hostile red, unfriendly orange, friendly green, allied blue, neutral grey.
 */
export type TokenTone = 'party' | 'hostile' | 'unfriendly' | 'friendly' | 'allied' | 'neutral';

export interface Token {
  charId: string;
  name: string;
  x: number;
  y: number;
  footprint: number;
  tone: TokenTone;
  isPlayer: boolean;
  isCurrentTurn: boolean;
  isDown: boolean;
  avatarUrl?: string;
  avatarEmoji?: string;
  healthLabel?: string;
}

export interface MapBounds {
  minX: number;
  minY: number;
  width: number;
  height: number;
}

/**
 * Cells on a side this creature occupies. `size` comes from the observable view
 * (every character seen mid-session); `permanent.size` covers the connect
 * snapshot, which ships the full permanent blob instead. Reading only the latter
 * rendered every spawned monster as 1x1 until the page was refreshed.
 */
export function footprintFor(char: GameCharacterInfo): number {
  const size = (char.size ?? (char.permanent?.size as string | undefined))?.toLowerCase();
  return (size && FOOTPRINT_BY_SIZE[size]) || 1;
}

/**
 * `unfriendly` used to fall through to the neutral tone, which made a creature
 * that dislikes you look exactly like a merchant. The board's ring is the only
 * standing cue about who means you harm, so the five attitudes each get their
 * own, matching the scene panel's badges.
 */
function toneFor(char: GameCharacterInfo, isParty: boolean): TokenTone {
  if (isParty) return 'party';
  switch (char.attitude) {
    case 'hostile':
      return 'hostile';
    case 'unfriendly':
      return 'unfriendly';
    case 'friendly':
      return 'friendly';
    case 'allied':
      return 'allied';
    default:
      return 'neutral';
  }
}

export function toToken(
  char: GameCharacterInfo,
  playerCharacterId: string | null,
  currentTurn: string | null
): Token | null {
  const pos = char.position;
  if (!pos || typeof pos.x !== 'number' || typeof pos.y !== 'number') return null;

  const isParty = char.isPartyMember === true;
  return {
    charId: char.charId,
    name: char.name,
    x: pos.x,
    y: pos.y,
    footprint: footprintFor(char),
    tone: toneFor(char, isParty),
    isPlayer: char.charId === playerCharacterId,
    isCurrentTurn: char.charId === currentTurn,
    isDown: char.isDead === true || char.healthState === 'dead' || char.healthState === 'dying',
    avatarUrl: char.avatarUrl,
    avatarEmoji: char.avatarEmoji,
    healthLabel: char.healthState,
  };
}

/**
 * Deduplicate by id (a party member can appear in both store slices) and drop
 * everyone without a grid position.
 */
export function tokensFrom(
  characters: GameCharacterInfo[],
  playerCharacterId: string | null,
  currentTurn: string | null
): Token[] {
  const byId = new Map<string, GameCharacterInfo>();
  for (const c of characters) {
    if (!byId.has(c.charId)) byId.set(c.charId, c);
  }
  return [...byId.values()]
    .map((c) => toToken(c, playerCharacterId, currentTurn))
    .filter((t): t is Token => t !== null);
}

/**
 * One token's readable description for the hover tooltip: who, how they regard
 * you, how hurt, how much space.
 *
 * The tone is spelled out rather than left to the ring colour, since colour
 * alone is no cue at all for a colourblind player. Party and neutral are the
 * two silent cases: a teammate needs no warning, and neutral is the absence of
 * one.
 *
 * Grid coordinates are deliberately absent. A sighted player is already looking
 * at the square the token sits on, so "(4, 7)" only adds noise to a tooltip
 * they opened to read a name and a health state. The screen-reader roster keeps
 * them — see [`tokenRosterLabel`].
 */
export function tokenLabel(token: Token): string {
  return buildTokenLabel(token, false);
}

function buildTokenLabel(token: Token, withPosition: boolean): string {
  return [
    token.name,
    token.tone === 'party' || token.tone === 'neutral' ? null : token.tone.toUpperCase(),
    withPosition ? `(${token.x}, ${token.y})` : null,
    token.healthLabel ? token.healthLabel.toUpperCase() : null,
    token.footprint > 1 ? `${token.footprint * FEET_PER_SQUARE} ft space` : null,
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * [`tokenLabel`] plus the grid position, for the screen-reader roster only.
 * Position is the one thing a non-sighted player cannot get any other way: the
 * board renders as a single `role="img"`, so there are no per-cell nodes to
 * navigate and no visual grid to read. Dropping coordinates here would leave
 * them knowing who is on the board and nothing about where.
 */
export function tokenRosterLabel(token: Token): string {
  return buildTokenLabel(token, true);
}

/**
 * The whole board as one sentence, for the container's `aria-label`. The board
 * is `role="img"` (a grid with no rows is invalid ARIA, and this panel has no
 * cell navigation), so assistive tech never reaches the per-token labels — the
 * roster has to be here or it is not announced at all.
 */
export function describeMap(tokens: Token[]): string {
  if (tokens.length === 0) return 'Tactical map, no combatants positioned';
  const roster = tokens.map(tokenRosterLabel).join('; ');
  return `Tactical map, ${tokens.length} combatant${tokens.length === 1 ? '' : 's'}: ${roster}`;
}

/**
 * Grid bounds derived from the occupied cells. A Large creature anchored at
 * (x, y) extends to (x + footprint - 1), so its far edge counts too or the
 * token would be clipped by the border.
 */
export function deriveBounds(tokens: Token[]): MapBounds | null {
  if (tokens.length === 0) return null;
  const minX = Math.min(...tokens.map((t) => t.x)) - MARGIN_CELLS;
  const minY = Math.min(...tokens.map((t) => t.y)) - MARGIN_CELLS;
  const maxX = Math.max(...tokens.map((t) => t.x + t.footprint - 1)) + MARGIN_CELLS;
  const maxY = Math.max(...tokens.map((t) => t.y + t.footprint - 1)) + MARGIN_CELLS;
  const width = Math.max(maxX - minX + 1, MIN_SPAN_CELLS);
  const height = Math.max(maxY - minY + 1, MIN_SPAN_CELLS);
  return { minX, minY, width, height };
}
